"use client";

import { c as _c } from "react/compiler-runtime";
import { motion, useSpring } from "motion/react";
import type { RefObject } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

// Spring config for smooth tooltip movement
const springConfig = {
  stiffness: 100,
  damping: 20
};
export interface TooltipBoxProps {
  /** X position in pixels (relative to container) */
  x: number;
  /** Y position in pixels (relative to container) */
  y: number;
  /** Whether the tooltip is visible */
  visible: boolean;
  /** Container ref for portal rendering */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Container width for flip detection */
  containerWidth: number;
  /** Container height for bounds clamping */
  containerHeight: number;
  /** Offset from the target position */
  offset?: number;
  /** Custom class name */
  className?: string;
  /** Tooltip content */
  children: React.ReactNode;
  /** Override left position (bypasses internal calculation) */
  left?: number | ReturnType<typeof useSpring>;
  /** Override top position (bypasses internal calculation) */
  top?: number | ReturnType<typeof useSpring>;
  /** Force flip direction (for custom positioning) */
  flipped?: boolean;
}
export function TooltipBox(t0) {
  const $ = _c(39);
  const {
    x,
    y,
    visible,
    containerRef,
    containerWidth,
    containerHeight,
    offset: t1,
    className: t2,
    children,
    left: leftOverride,
    top: topOverride,
    flipped: flippedOverride
  } = t0;
  const offset = t1 === undefined ? 16 : t1;
  const className = t2 === undefined ? "" : t2;
  const tooltipRef = useRef(null);
  const [tooltipWidth, setTooltipWidth] = useState(180);
  const [tooltipHeight, setTooltipHeight] = useState(80);
  const [portalContainer, setPortalContainer] = useState(null);
  if (containerRef.current && containerRef.current !== portalContainer) {
    setPortalContainer(containerRef.current);
  }
  let t3;
  let t4;
  if ($[0] !== tooltipHeight || $[1] !== tooltipWidth) {
    t3 = () => {
      if (tooltipRef.current) {
        const w = tooltipRef.current.offsetWidth;
        const h = tooltipRef.current.offsetHeight;
        if (w > 0 && w !== tooltipWidth) {
          setTooltipWidth(w);
        }
        if (h > 0 && h !== tooltipHeight) {
          setTooltipHeight(h);
        }
      }
    };
    t4 = [tooltipWidth, tooltipHeight];
    $[0] = tooltipHeight;
    $[1] = tooltipWidth;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useLayoutEffect(t3, t4);
  const shouldFlipX = x + tooltipWidth + offset > containerWidth;
  const targetX = shouldFlipX ? x - offset - tooltipWidth : x + offset;
  const targetY = Math.max(offset, Math.min(y - tooltipHeight / 2, containerHeight - tooltipHeight - offset));
  const animatedLeft = useSpring(targetX, springConfig);
  const animatedTop = useSpring(targetY, springConfig);
  let t5;
  let t6;
  if ($[4] !== animatedLeft || $[5] !== targetX) {
    t5 = () => {
      animatedLeft.set(targetX);
    };
    t6 = [targetX, animatedLeft];
    $[4] = animatedLeft;
    $[5] = targetX;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[8] !== animatedTop || $[9] !== targetY) {
    t7 = () => {
      animatedTop.set(targetY);
    };
    t8 = [targetY, animatedTop];
    $[8] = animatedTop;
    $[9] = targetY;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  const finalLeft = leftOverride ?? animatedLeft;
  const finalTop = topOverride ?? animatedTop;
  const isFlipped = flippedOverride ?? shouldFlipX;
  const flipKey = isFlipped ? "flipped" : "default";
  const transformOrigin = isFlipped ? "right top" : "left top";
  if (!portalContainer) {
    return null;
  }
  if (!visible) {
    return null;
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: 1
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== className) {
    t10 = cn("pointer-events-none absolute z-50", className);
    $[13] = className;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 0
    };
    t12 = {
      opacity: 0
    };
    $[15] = t11;
    $[16] = t12;
  } else {
    t11 = $[15];
    t12 = $[16];
  }
  let t13;
  if ($[17] !== finalLeft || $[18] !== finalTop) {
    t13 = {
      left: finalLeft,
      top: finalTop
    };
    $[17] = finalLeft;
    $[18] = finalTop;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      duration: 0.1
    };
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      scale: 1,
      opacity: 1,
      x: 0
    };
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = isFlipped ? 20 : -20;
  let t17;
  if ($[22] !== t16) {
    t17 = {
      scale: 0.85,
      opacity: 0,
      x: t16
    };
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== transformOrigin) {
    t18 = {
      transformOrigin
    };
    $[24] = transformOrigin;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      type: "spring",
      stiffness: 300,
      damping: 25
    };
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== children || $[28] !== flipKey || $[29] !== t17 || $[30] !== t18) {
    t20 = <motion.div animate={t15} className="shadow-ds-float-deep min-w-[140px] overflow-hidden rounded-xl border border-white/10 bg-[var(--chart-tooltip-background)] text-[var(--chart-tooltip-foreground)] backdrop-blur-xl" initial={t17} key={flipKey} style={t18} transition={t19}>{children}</motion.div>;
    $[27] = children;
    $[28] = flipKey;
    $[29] = t17;
    $[30] = t18;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== t10 || $[33] !== t13 || $[34] !== t20) {
    t21 = <motion.div animate={t9} className={t10} exit={t11} initial={t12} ref={tooltipRef} style={t13} transition={t14}>{t20}</motion.div>;
    $[32] = t10;
    $[33] = t13;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== portalContainer || $[37] !== t21) {
    t22 = createPortal(t21, portalContainer);
    $[36] = portalContainer;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  return t22;
}
TooltipBox.displayName = "TooltipBox";
export default TooltipBox;